/**
 * Firebase Storage service for media asset management
 */
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  getMetadata,
  updateMetadata,
  listAll,
  type UploadTask,
  type StorageReference,
  type FullMetadata,
} from 'firebase/storage';
import type { FirebaseApp } from 'firebase/app';

import type {
  UploadProgress,
  UploadResult,
  StorageError,
  StorageMetadata,
  PosterUploadOptions,
  GroupPosterUploadOptions,
  PosterAsset,
} from './types';
import { STORAGE_PATHS, CACHE_HEADERS } from './config';
import { validatePosterFile, validateAltText, generateSafeFileName } from './validation';

export class StorageService {
  private storage;

  constructor(app: FirebaseApp) {
    this.storage = getStorage(app);
  }

  /**
   * Upload a poster image for a meeting with resumable upload
   */
  async uploadPosterImage(options: PosterUploadOptions): Promise<UploadResult> {
    const { groupId, meetingId, file, altText, onProgress, onError, metadata } = options;

    try {
      // Validate the file and alt text
      validatePosterFile(file);
      validateAltText(altText);

      // Generate safe filename
      const safeFileName = generateSafeFileName(file.name, 'poster');
      const storagePath = STORAGE_PATHS.posters(groupId, meetingId, safeFileName);

      // Create storage reference
      const storageRef = ref(this.storage, storagePath);

      // Prepare metadata
      const uploadMetadata: StorageMetadata = {
        contentType: file.type,
        cacheControl: CACHE_HEADERS.posters,
        customMetadata: {
          originalName: file.name,
          uploadedBy: 'web-client',
          groupId,
          meetingId,
          altText: altText || '',
          ...metadata?.customMetadata,
        },
        ...metadata,
      };

      // Start resumable upload
      const uploadTask = uploadBytesResumable(storageRef, file, uploadMetadata);

      return new Promise<UploadResult>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          snapshot => {
            const progress: UploadProgress = {
              bytesTransferred: snapshot.bytesTransferred,
              totalBytes: snapshot.totalBytes,
              percentage: Math.round(
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
              ),
              state: snapshot.state as UploadProgress['state'],
            };

            onProgress?.(progress);
          },
          error => {
            const storageError = this.handleStorageError(error);
            onError?.(storageError);
            reject(storageError);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              const result: UploadResult = {
                url: downloadURL,
                storagePath,
                fileName: safeFileName,
                contentType: file.type,
                sizeBytes: file.size,
              };
              resolve(result);
            } catch (error) {
              const storageError = this.handleStorageError(error);
              onError?.(storageError);
              reject(storageError);
            }
          },
        );
      });
    } catch (error) {
      const storageError = this.handleStorageError(error);
      onError?.(storageError);
      throw storageError;
    }
  }

  /**
   * Delete a poster image
   */
  async deletePosterImage(storagePath: string): Promise<void> {
    try {
      const storageRef = ref(this.storage, storagePath);
      await deleteObject(storageRef);
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }

  /**
   * Get poster asset information
   */
  async getPosterAsset(storagePath: string): Promise<PosterAsset | null> {
    try {
      const storageRef = ref(this.storage, storagePath);
      const [metadata, downloadURL] = await Promise.all([
        getMetadata(storageRef),
        getDownloadURL(storageRef),
      ]);

      return {
        url: downloadURL,
        storagePath,
        altText: metadata.customMetadata?.altText,
        fileName: metadata.name,
        contentType: metadata.contentType || 'image/jpeg',
        sizeBytes: metadata.size,
        uploadedAt: metadata.timeCreated,
      };
    } catch (error) {
      if (this.isNotFoundError(error)) {
        return null;
      }
      throw this.handleStorageError(error);
    }
  }

  /**
   * List all poster assets for a meeting
   */
  async listMeetingPosters(groupId: string, meetingId: string): Promise<PosterAsset[]> {
    try {
      const folderPath = `posters/meetings/${groupId}/${meetingId}/`;
      const folderRef = ref(this.storage, folderPath);
      const listResult = await listAll(folderRef);

      const posterAssets = await Promise.allSettled(
        listResult.items.map(async itemRef => {
          const [metadata, downloadURL] = await Promise.all([
            getMetadata(itemRef),
            getDownloadURL(itemRef),
          ]);

          return {
            url: downloadURL,
            storagePath: itemRef.fullPath,
            altText: metadata.customMetadata?.altText,
            fileName: metadata.name,
            contentType: metadata.contentType || 'image/jpeg',
            sizeBytes: metadata.size,
            uploadedAt: metadata.timeCreated,
          } as PosterAsset;
        }),
      );

      return posterAssets
        .filter(
          (result): result is PromiseFulfilledResult<PosterAsset> =>
            result.status === 'fulfilled',
        )
        .map(result => result.value);
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }

  /**
   * Upload a poster image for a group with resumable upload
   */
  async uploadGroupPosterImage(options: GroupPosterUploadOptions): Promise<UploadResult> {
    const { groupId, file, altText, onProgress, onError, metadata } = options;

    try {
      // Validate the file and alt text
      validatePosterFile(file);
      validateAltText(altText);

      // Generate safe filename
      const safeFileName = generateSafeFileName(file.name, 'group-poster');
      const storagePath = STORAGE_PATHS.groupPosters(groupId, safeFileName);

      // Create storage reference
      const storageRef = ref(this.storage, storagePath);

      // Prepare metadata
      const uploadMetadata: StorageMetadata = {
        contentType: file.type,
        cacheControl: CACHE_HEADERS.posters,
        customMetadata: {
          originalName: file.name,
          uploadedBy: 'web-client',
          groupId,
          altText: altText || '',
          ...metadata?.customMetadata,
        },
        ...metadata,
      };

      // Start resumable upload
      const uploadTask = uploadBytesResumable(storageRef, file, uploadMetadata);

      return new Promise<UploadResult>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          snapshot => {
            const progress: UploadProgress = {
              bytesTransferred: snapshot.bytesTransferred,
              totalBytes: snapshot.totalBytes,
              percentage: Math.round(
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
              ),
              state: snapshot.state as UploadProgress['state'],
            };

            onProgress?.(progress);
          },
          error => {
            const storageError = this.handleStorageError(error);
            onError?.(storageError);
            reject(storageError);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              const result: UploadResult = {
                url: downloadURL,
                storagePath,
                fileName: safeFileName,
                contentType: file.type,
                sizeBytes: file.size,
              };
              resolve(result);
            } catch (error) {
              const storageError = this.handleStorageError(error);
              onError?.(storageError);
              reject(storageError);
            }
          },
        );
      });
    } catch (error) {
      const storageError = this.handleStorageError(error);
      onError?.(storageError);
      throw storageError;
    }
  }

  /**
   * List all poster assets for a group
   */
  async listGroupPosters(groupId: string): Promise<PosterAsset[]> {
    try {
      const folderPath = `posters/groups/${groupId}/`;
      const folderRef = ref(this.storage, folderPath);
      const listResult = await listAll(folderRef);

      const posterAssets = await Promise.allSettled(
        listResult.items.map(async itemRef => {
          const [metadata, downloadURL] = await Promise.all([
            getMetadata(itemRef),
            getDownloadURL(itemRef),
          ]);

          return {
            url: downloadURL,
            storagePath: itemRef.fullPath,
            altText: metadata.customMetadata?.altText,
            fileName: metadata.name,
            contentType: metadata.contentType || 'image/jpeg',
            sizeBytes: metadata.size,
            uploadedAt: metadata.timeCreated,
          } as PosterAsset;
        }),
      );

      return posterAssets
        .filter(
          (result): result is PromiseFulfilledResult<PosterAsset> =>
            result.status === 'fulfilled',
        )
        .map(result => result.value);
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }

  /**
   * Update poster metadata
   */
  async updatePosterMetadata(
    storagePath: string,
    metadata: Partial<StorageMetadata>,
  ): Promise<void> {
    try {
      const storageRef = ref(this.storage, storagePath);
      await updateMetadata(storageRef, metadata);
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }

  /**
   * Get a signed URL for secure access (useful for temporary access)
   */
  async getSignedUrl(storagePath: string): Promise<string> {
    try {
      const storageRef = ref(this.storage, storagePath);
      return await getDownloadURL(storageRef);
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }

  /**
   * Upload to temporary storage (for processing workflows)
   */
  async uploadToTempStorage(
    userId: string,
    file: File,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<UploadResult> {
    try {
      validatePosterFile(file);

      const safeFileName = generateSafeFileName(file.name, 'temp');
      const storagePath = STORAGE_PATHS.tempPosters(userId, safeFileName);
      const storageRef = ref(this.storage, storagePath);

      const metadata: StorageMetadata = {
        contentType: file.type,
        cacheControl: CACHE_HEADERS.temp,
        customMetadata: {
          originalName: file.name,
          userId,
          uploadType: 'temporary',
        },
      };

      const uploadTask = uploadBytesResumable(storageRef, file, metadata);

      return new Promise<UploadResult>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          snapshot => {
            const progress: UploadProgress = {
              bytesTransferred: snapshot.bytesTransferred,
              totalBytes: snapshot.totalBytes,
              percentage: Math.round(
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
              ),
              state: snapshot.state as UploadProgress['state'],
            };

            onProgress?.(progress);
          },
          error => reject(this.handleStorageError(error)),
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                url: downloadURL,
                storagePath,
                fileName: safeFileName,
                contentType: file.type,
                sizeBytes: file.size,
              });
            } catch (error) {
              reject(this.handleStorageError(error));
            }
          },
        );
      });
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }

  /**
   * Clean up temporary files older than specified time
   */
  async cleanupTempFiles(userId: string, maxAgeHours = 24): Promise<number> {
    try {
      const tempFolderPath = `temp/posters/${userId}/`;
      const tempFolderRef = ref(this.storage, tempFolderPath);
      const listResult = await listAll(tempFolderRef);

      const cutoffTime = Date.now() - maxAgeHours * 60 * 60 * 1000;
      let deletedCount = 0;

      await Promise.allSettled(
        listResult.items.map(async itemRef => {
          try {
            const metadata = await getMetadata(itemRef);
            const createdTime = new Date(metadata.timeCreated).getTime();

            if (createdTime < cutoffTime) {
              await deleteObject(itemRef);
              deletedCount++;
            }
          } catch (error) {
            console.warn(`Failed to process temp file ${itemRef.fullPath}:`, error);
          }
        }),
      );

      return deletedCount;
    } catch (error) {
      throw this.handleStorageError(error);
    }
  }

  /**
   * Handle and standardize Firebase Storage errors
   */
  private handleStorageError(error: any): StorageError {
    const storageError: StorageError = {
      name: 'StorageError',
      message: error.message || 'Unknown storage error occurred',
      code: error.code || 'storage/unknown',
      serverResponse: error.serverResponse,
      httpStatusCode: error.httpStatusCode,
    };

    // Add user-friendly messages for common errors
    switch (error.code) {
      case 'storage/unauthorized':
        storageError.message = 'You do not have permission to perform this action';
        break;
      case 'storage/canceled':
        storageError.message = 'Upload was cancelled';
        break;
      case 'storage/quota-exceeded':
        storageError.message = 'Storage quota exceeded';
        break;
      case 'storage/invalid-url':
        storageError.message = 'Invalid storage URL';
        break;
      case 'storage/no-default-bucket':
        storageError.message = 'Storage bucket not configured';
        break;
      case 'storage/object-not-found':
        storageError.message = 'File not found';
        break;
      default:
        if (error instanceof Error) {
          storageError.message = error.message;
        }
    }

    return storageError;
  }

  /**
   * Check if error is a "not found" error
   */
  private isNotFoundError(error: any): boolean {
    return error.code === 'storage/object-not-found';
  }
}
